//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useMemo } from 'react';
import { UIContext, type UIContextValue } from '@/contexts/UIContext';
import { useAuth } from '@/hooks/useAuth';
import { usePersistentState } from '@/hooks/usePersistentState';
import { useProfilePreference } from '@/hooks/useProfilePreference';

// Whether the console report is offered at all.  Development only: it is a tool for working
// on the renderer, it writes several lines every two seconds, and reading it costs a walk of
// the whole scene -- which is not something a release should be able to switch on.
const OFFER_DIAGNOSTICS = import.meta.env.DEV;

interface UIProviderProps {
  children: React.ReactNode;
}

// Owns app-level UI preferences: the nav display mode, and what a graph says about
// how it is being drawn.  The latter two live here rather than in the graph that
// shows them so that the settings pane and every graph on screen are reading one
// value -- two components on the same stored key each keep their own copy of it.
const UIProvider = ({ children }: UIProviderProps) => {
  const { userProfile } = useAuth();
  // On the profile, for the same reason showFrameRate is: set on the settings page, about how
  // a person likes to work, and expected back on another machine. Held as component state it
  // survived no reload at all.
  const displayMode = userProfile?.displayMode ?? 'icon-titles';
  const setDisplayMode = useProfilePreference('displayMode', 'icon-titles');
  // On the profile rather than in this browser: it is a preference set on the settings page,
  // where everything else about how a person likes to work is kept, and one they would expect
  // to find again on another machine.
  const showFrameRate = userProfile?.showFrameRate ?? false;
  const setShowFrameRate = useProfilePreference('showFrameRate', false);
  // And this one stays in the browser, being a developer's switch rather than a preference
  // (see OFFER_DIAGNOSTICS).
  const [logGraphDiagnostics, setLogGraphDiagnostics] =
    usePersistentState<boolean>('graph-diagnostics', false);

  const value = useMemo((): UIContextValue => ({
    displayMode, setDisplayMode, showFrameRate, setShowFrameRate,
    // Off in a built copy whatever was stored: what it writes to the console is for whoever
    // is working on the renderer, and it costs a walk of the whole scene every two seconds.
    logGraphDiagnostics: OFFER_DIAGNOSTICS && logGraphDiagnostics,
    setLogGraphDiagnostics
  }), [displayMode, setDisplayMode, showFrameRate, setShowFrameRate, logGraphDiagnostics,
       setLogGraphDiagnostics]);

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

export default UIProvider;
